import { percent, Root } from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from '@amcharts/amcharts5/xy';
import { useEffect, useState } from 'react';
import { ChartOption } from '../../../interface/reports';
import { ExecutionStatus } from '../../../resources/executions/executions-types';
import { StepErrorProcessorCounts, StepErrors } from '../../../resources/step-errors/step-errors-model';
import { getStatusColor } from '../../../utils/execution-utils';
import { Item } from '../../ui/dropdown-btn';

type Props = {
  id: string;
  data: StepErrors[];
  activeChart: Item | null | undefined;
};

export default function StepErrorsSortedBarChartComponent({ id, data, activeChart }: Props) {
  const [root, setRoot] = useState<Root>();
  const [chart, setChart] = useState<XYChart>();
  const [yAxis, setYAxis] = useState<CategoryAxis<AxisRenderer>>();
  const [series, setSeries] = useState<ColumnSeries>();

  const toTitleCase = (str: string) => {
    // Capitalize the first letter of each word
    if (str) {
      return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    }
    return str;
  };

  // Use Execution Utils to get the appropriate color for an execution status
  const getStepStatusColor = (stepStatus: string) => {
    const stepStatusEnumValue = stepStatus as ExecutionStatus;
    let statusColor = getStatusColor(stepStatusEnumValue); // Get color configuration for status

    const match = statusColor.match(/var\((--[^)]+)\)/); // If enclosed in var(), remove it
    statusColor = match ? match[1] : statusColor;

    // Get raw color value from CSS var, if applicable
    const computedColor = getComputedStyle(document.documentElement).getPropertyValue(statusColor).trim();

    // If no computed style could be found, then raw hex was configured for that status
    if (computedColor === '') {
      return statusColor;
    }
    return computedColor;
  };

  useEffect(() => {
    data.sort((a, b) => {
      return a.count - b.count;
    });
  }, [data]);

  useEffect(() => {
    const newRoot = Root.new(id);
    setRoot(newRoot);

    // Cleanup function to dispose root on unmount
    return () => {
      newRoot.dispose();
    };
  }, [id]);

  useEffect(() => {
    if (root && !chart) {
      // Set themes
      root.setThemes([am5themes_Animated.new(root)]);

      // Create chart
      const chart = root.container.children.push(
        XYChart.new(root, {
          panX: false,
          panY: true,
          wheelX: 'none',
          wheelY: 'zoomY',
        }),
      );

      // We don't want zoom-out button to appear while animating, so we hide it
      chart.zoomOutButton.set('forceHidden', true);

      setChart(chart);
    }
  }, [root, chart]);

  useEffect(() => {
    if (root && chart) {
      // Create axes
      const yRenderer = AxisRendererY.new(root, {
        minGridDistance: 30,
      });

      const yAxis = chart.yAxes.push(
        CategoryAxis.new(root, {
          maxDeviation: 0,
          categoryField: 'stepName',
          renderer: yRenderer as AxisRenderer,
        }),
      );

      const xAxis = chart.xAxes.push(
        ValueAxis.new(root, {
          maxDeviation: 0,
          min: 0,
          extraMax: 0.1,
          renderer: AxisRendererX.new(root, {}) as AxisRenderer,
        }),
      );

      const series = chart.series.push(
        ColumnSeries.new(root, {
          name: 'Series 1',
          xAxis,
          yAxis,
          valueXField: 'count',
          categoryYField: 'stepName',
        }),
      );

      // Rounded corners for columns
      series.columns.template.setAll({
        cornerRadiusTR: 5,
        cornerRadiusBR: 5,
      });

      // Make each column to be of a different color
      series.columns.template.adapters.add('fill', (_fill, target) =>
        chart.get('colors')?.getIndex(series.columns.indexOf(target as any)),
      );

      series.columns.template.adapters.add('stroke', (_stroke, target) =>
        chart.get('colors')?.getIndex(series.columns.indexOf(target as any)),
      );

      series.columns.template.setAll({
        tooltipText: 'Total Errors: [bold]{valueX}[/]\n\n[bold]Errors by Processor:[/]\n{additionalInfo}',
        width: percent(40),
        strokeOpacity: 0,
      });

      // Add tooltip data
      series.columns.template.adapters.add('tooltipText', (text, target) => {
        const dataItem = target.dataItem;
        if (dataItem) {
          const dataContext = dataItem.dataContext as { [key: string]: any };
          let additionalInfo = '';

          // Append processor status counts to tooltip info
          const processorCounts: StepErrorProcessorCounts[] = dataContext['processorCounts'];

          if (processorCounts) {
            processorCounts.forEach((processorCount, index) => {
              additionalInfo += `${processorCount.processorName}: [bold]${processorCount.count}[/]`;
              additionalInfo += index === processorCounts.length - 1 ? '' : '\n';
            });
          }

          return text?.replace('{additionalInfo}', additionalInfo.trim());
        }
        return text;
      });

      // Convert y axis label to title case and append step status
      yAxis.get('renderer').labels.template.adapters.add('text', (text, target) => {
        const targetDataContext = target.dataItem?.dataContext as { [key: string]: any };
        if (targetDataContext) {
          const stepStatus = targetDataContext['status'] as string;
          const stepStatusColor = getStepStatusColor(stepStatus);

          let formattedStepName = toTitleCase(targetDataContext['stepName']);
          formattedStepName = formattedStepName.replace(/\([A-Za-z_]+\)/, '');
          const formattedStatus = toTitleCase((targetDataContext['status'] as string).replace('_', ' ').toLowerCase());
          const nameWithStatus = formattedStepName + ` ([${stepStatusColor}]${formattedStatus}[/])`;
          return nameWithStatus;
        }
        return text;
      });

      chart.set(
        'cursor',
        XYCursor.new(root, {
          behavior: 'none',
          xAxis,
          yAxis,
        }),
      );

      setYAxis(yAxis);
      setSeries(series);
    }
  }, [root, chart]);

  useEffect(() => {
    if (chart && series && yAxis && series && data.length) {
      yAxis.data.setAll(data);
      series.data.setAll(data);

      // Make stuff animate on load
      series.appear(1000);
      chart.appear(1000, 100);
    }
  }, [chart, yAxis, series, data]);

  return (
    <div
      hidden={activeChart?.id !== ChartOption[ChartOption.PROCESSOR_STEP_ERRORS]}
      id={id}
      style={{ height: `${200 + data.length * 30}px` }}
    />
  );
}
